import { defineStore } from 'pinia'
import config from '../../config'
import { setI18nLanguage, getCurrentLanguage } from '@/i18n'

export const useIdiomasStore = defineStore('idiomas', {
  state: () => ({
    idiomas: [],
    idiomaActual: null,
    loading: false,
    error: null
  }),

  getters: {
    getIdiomasActivos: (state) => state.idiomas.filter((idioma) => idioma.activo),

    getIdiomaActualInfo: (state) => {
      return state.idiomas.find((idioma) => idioma.codigo_iso === getCurrentLanguage())
    }
  },

  actions: {
    async cargarIdiomas() {
      this.loading = true
      this.error = null

      try {
        const token = localStorage.getItem('accesToken')
        const headers = {
          'Content-Type': 'application/json'
        }

        // Agregar token si está disponible (usuario logueado)
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }

        const response = await fetch(config.apiBaseUrl + '/idiomas/activos', {
          headers
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        this.idiomas = await response.json()

        // Si no hay idioma actual, establecer el por defecto
        if (!this.idiomaActual) {
          const idiomaGuardado = localStorage.getItem('i18n-language')
          const idiomaPorDefecto = this.idiomas.find((i) => i.codigo_iso === 'en') || this.idiomas[0]

          if (idiomaGuardado) {
            const idioma = this.idiomas.find((i) => i.codigo_iso === idiomaGuardado)
            this.idiomaActual = idioma || idiomaPorDefecto
          } else {
            this.idiomaActual = idiomaPorDefecto
          }
        }
      } catch (error) {
        this.error = error.message || 'Error al cargar idiomas'
        console.error('Error cargando idiomas:', error)
      } finally {
        this.loading = false
      }
    },

    async cambiarIdioma(idiomaId) {
      this.loading = true
      this.error = null

      try {
        // Buscar el idioma por ID
        const idioma = this.idiomas.find((i) => i.id === idiomaId)
        if (!idioma) {
          throw new Error('Idioma no encontrado')
        }

        // Obtener token de autenticación
        const token = localStorage.getItem('accesToken')

        // Cambiar idioma en el backend para el usuario actual (si está logueado)
        if (token) {
          await this.cambiarIdiomaUsuarioBD(idiomaId)
        }

        // Cambiar idioma en el frontend
        setI18nLanguage(idioma.codigo_iso)
        this.idiomaActual = idioma

        return { success: true, message: 'Idioma cambiado exitosamente' }
      } catch (error) {
        this.error = error.message || 'Error al cambiar idioma'
        console.error('Error cambiando idioma:', error)
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    // Función separada para cambiar idioma en BD (sin cambiar frontend)
    async cambiarIdiomaUsuarioBD(idiomaId) {
      const token = localStorage.getItem('accesToken')
      if (!token) return

      const response = await fetch(config.apiBaseUrl + '/usuario/cambiar-idioma', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idioma_id: idiomaId
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
    },

    establecerIdiomaDesdeUsuario(usuario) {
      if (usuario && usuario.idioma) {
        const idioma = this.idiomas.find((i) => i.id === usuario.idioma.id)
        if (idioma) {
          setI18nLanguage(idioma.codigo_iso)
          this.idiomaActual = idioma
        }
      } else {
        // Si el usuario no tiene idioma, usar inglés por defecto
        const idiomaDefecto = this.idiomas.find((i) => i.codigo_iso === 'en')
        if (idiomaDefecto) {
          setI18nLanguage('en')
          this.idiomaActual = idiomaDefecto
        }
      }
    },

    // Para administradores - gestión completa de idiomas
    async obtenerTodosIdiomas(params = {}) {
      this.loading = true
      this.error = null

      try {
        const token = localStorage.getItem('accesToken')
        const queryParams = new URLSearchParams(params).toString()
        const url = config.apiBaseUrl + '/idiomas' + (queryParams ? `?${queryParams}` : '')

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        return await response.json()
      } catch (error) {
        this.error = error.message || 'Error al obtener idiomas'
        throw error
      } finally {
        this.loading = false
      }
    },

    async crearIdioma(idioma) {
      this.loading = true
      this.error = null

      try {
        const token = localStorage.getItem('accesToken')
        const response = await fetch(config.apiBaseUrl + '/idiomas', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(idioma)
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        // Recargar la lista de idiomas
        await this.cargarIdiomas()
        return data
      } catch (error) {
        this.error = error.message || 'Error al crear idioma'
        throw error
      } finally {
        this.loading = false
      }
    },

    async actualizarIdioma(id, idioma) {
      this.loading = true
      this.error = null

      try {
        const token = localStorage.getItem('accesToken')
        const response = await fetch(config.apiBaseUrl + `/idiomas/${id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(idioma)
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        // Recargar la lista de idiomas
        await this.cargarIdiomas()
        return data
      } catch (error) {
        this.error = error.message || 'Error al actualizar idioma'
        throw error
      } finally {
        this.loading = false
      }
    },

    async eliminarIdioma(id) {
      this.loading = true
      this.error = null

      try {
        const token = localStorage.getItem('accesToken')
        const response = await fetch(config.apiBaseUrl + `/idiomas/${id}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        // Recargar la lista de idiomas
        await this.cargarIdiomas()
        return data
      } catch (error) {
        this.error = error.message || 'Error al eliminar idioma'
        throw error
      } finally {
        this.loading = false
      }
    },

    // Función para inicializar el store
    async initialize() {
      await this.cargarIdiomas()
    },

    // Función para establecer idioma actual
    async setCurrentLanguage(codigoIso) {
      const idioma = this.idiomas.find((i) => i.codigo_iso === codigoIso)
      if (idioma) {
        this.idiomaActual = idioma
        setI18nLanguage(codigoIso)
        localStorage.setItem('i18n-language', codigoIso)
      }
    }
  }
})
